<template>
  <LibraryLayout>
    <ArticlesBlock
      v-if="favoritesViewRef && favoritesViewRef.dataLength"
      :view="favoritesViewRef"
    />
    <EmptyListingBlock v-else title="Favorites" class="empty-favorite">
      <div>
        <p>Articles you've marked as a Favorite will appear here.</p>
        <p>
          To mark an article as a Favorite, click the bookmark icon on the
          article card or while reading the article.
        </p>
      </div>
    </EmptyListingBlock>
    <ArticlesBlock
      v-if="viewedViewRef && viewedViewRef.dataLength"
      :view="viewedViewRef"
    />
    <NoCurrentlyReadingDocsBlock v-else title="Viewed">
      <p>
        Articles you're currently reading will appear here. Find new things to
        read on the Discover page!
      </p>
    </NoCurrentlyReadingDocsBlock>
  </LibraryLayout>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'
import type { Raw } from 'vue'

import ArticlesBlock from '@/app/components/listing/ArticlesBlock.vue'
import NoCurrentlyReadingDocsBlock from '@/app/components/listing/NoCurrentlyReadingDocsBlock.vue'
import EmptyListingBlock from '@/app/components/listing/EmptyListingBlock.vue'

import { filteredDocsByDocType } from '@/models/doc'

import { useWait } from '@/app/composables/useWait'
import type { Doc, DocsUserDataResponse } from '@/models/interfaces'
import LibraryLayout from '@/app/components/library/LibraryLayout.vue'
import { ArticlesView } from '@/models/doc.article'
import { DocSortOption, DocsViewMediator } from '@/models/doc'
import { ListView } from '@/models/docs.list'

const props = defineProps<{
  docs: Doc[]
  docsUserData: DocsUserDataResponse
}>()

const wait = useWait()

const viewedViewRef = ref<Raw<ListView> | null>(null)
const favoritesViewRef = ref<Raw<ListView> | null>(null)

onBeforeMount(async () => {
  await getArticles()
})

const getArticles = async (): Promise<void> => {
  wait.start('Loading Library')
  try {
    /* TODO: We need to implement offline support for home/docs, #12899.
    
    if (offline.isOffline) {
       await loadBookListFromNativeStorage()
     } */

    const articleData = filteredDocsByDocType(props.docs, 'article')

    const viewed = await ArticlesView.createView(
      articleData,
      props.docsUserData,
      false,
    )
    viewed.enableContinueReading()
    viewed.enableActionsButton()
    await viewed.setList('viewed')
    await viewed.sort(DocSortOption.accessed)
    viewedViewRef.value = new ListView('Viewed', viewed, {
      viewAllLink: '/app/articles/list/viewed',
    })

    const favorites = await ArticlesView.createView(
      articleData,
      props.docsUserData,
      false,
    )
    await favorites.setIsFavorite(true)
    favoritesViewRef.value = new ListView('Favorites', favorites, {
      viewAllLink: '/app/articles/list/favorites',
    })

    // The list of article-list views on this page. They all share the same
    // articlesData.
    // We create this but we don't need to store it anywhere; the views
    // hold references to it.
    new DocsViewMediator([viewed, favorites])

    wait.end('Loading Library')
  } catch (error) {
    if (Sentry) {
      Sentry.captureException(error)
    }
    wait.end('Loading Library')
    wait.start('Error Loading Library')
  }
}
</script>

<style scoped lang="scss">
.empty-favorite {
  :deep(.empty-listing__inner) {
    height: 390px;
    justify-content: center;

    @media only screen and (max-width: $w-tablet-s) {
      height: 335px;
    }
  }
}
</style>
